import React, { useRef, useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { 
  generatePDFFromElement 
} from "../../../utils/invoiceUtils";

const ViewCurriculumProgressReportModal = ({ 
  isOpen, 
  onClose, 
  campusId,
  sectionId, 
  isSectionReport, 
  sectionName, 
  className,
  campusName
}) => {
  const reportRef = useRef();
  const [loading, setLoading] = useState(false);

  // Dummy data for curriculum progress - Section level
  const dummyCurriculumProgressReport = {
    campusName: campusName || "Main Campus",
    className: className || "Grade 10",
    sectionName: sectionName || "Section A",
    reportDate: new Date().toLocaleDateString('en-GB'),
    subjects: [
      {
        id: "MATH-001",
        subjectName: "Mathematics",
        teacher: "Mr. Johnson",
        totalLessonsPlanned: 120,
        lessonsCompleted: 96,
        completionPercentage: 80,
        topics: [
          { name: "Algebra", planned: 30, completed: 28, percentage: 93.3 },
          { name: "Geometry", planned: 25, completed: 22, percentage: 88 },
          { name: "Trigonometry", planned: 20, completed: 15, percentage: 75 },
          { name: "Statistics", planned: 25, completed: 18, percentage: 72 },
          { name: "Calculus", planned: 20, completed: 13, percentage: 65 }
        ]
      },
      {
        id: "ENG-002",
        subjectName: "English Language",
        teacher: "Mrs. Smith",
        totalLessonsPlanned: 100,
        lessonsCompleted: 85,
        completionPercentage: 85,
        topics: [
          { name: "Grammar", planned: 25, completed: 23, percentage: 92 },
          { name: "Literature", planned: 30, completed: 26, percentage: 86.7 },
          { name: "Writing Skills", planned: 20, completed: 16, percentage: 80 },
          { name: "Reading Comprehension", planned: 25, completed: 20, percentage: 80 }
        ]
      },
      {
        id: "SCI-003",
        subjectName: "Physics",
        teacher: "Dr. Brown",
        totalLessonsPlanned: 90,
        lessonsCompleted: 45,
        completionPercentage: 50,
        topics: [
          { name: "Mechanics", planned: 25, completed: 15, percentage: 60 },
          { name: "Thermodynamics", planned: 20, completed: 12, percentage: 60 },
          { name: "Electromagnetism", planned: 25, completed: 10, percentage: 40 },
          { name: "Optics", planned: 20, completed: 8, percentage: 40 }
        ]
      },
      {
        id: "CHEM-004",
        subjectName: "Chemistry",
        teacher: "Ms. Davis",
        totalLessonsPlanned: 85,
        lessonsCompleted: 60,
        completionPercentage: 70.6,
        topics: [
          { name: "Organic Chemistry", planned: 30, completed: 18, percentage: 60 },
          { name: "Inorganic Chemistry", planned: 25, completed: 20, percentage: 80 },
          { name: "Physical Chemistry", planned: 20, completed: 15, percentage: 75 },
          { name: "Environmental Chemistry", planned: 10, completed: 7, percentage: 70 }
        ]
      },
      {
        id: "BIO-005",
        subjectName: "Biology",
        teacher: "Mr. Wilson",
        totalLessonsPlanned: 95,
        lessonsCompleted: 88,
        completionPercentage: 92.6,
        topics: [
          { name: "Cell Biology", planned: 20, completed: 19, percentage: 95 },
          { name: "Genetics", planned: 25, completed: 24, percentage: 96 },
          { name: "Evolution", planned: 15, completed: 14, percentage: 93.3 },
          { name: "Ecology", planned: 20, completed: 18, percentage: 90 },
          { name: "Human Physiology", planned: 15, completed: 13, percentage: 86.7 }
        ]
      },
      {
        id: "HIST-006",
        subjectName: "History",
        teacher: "Mrs. Johnson",
        totalLessonsPlanned: 80,
        lessonsCompleted: 40,
        completionPercentage: 50,
        topics: [
          { name: "Ancient Civilizations", planned: 20, completed: 12, percentage: 60 },
          { name: "Medieval Period", planned: 25, completed: 15, percentage: 60 },
          { name: "Modern History", planned: 20, completed: 8, percentage: 40 },
          { name: "World Wars", planned: 15, completed: 5, percentage: 33.3 }
        ]
      }
    ]
  };

  // Calculate overall statistics
  const calculateOverallStats = (subjects) => {
    const totalPlanned = subjects.reduce((sum, subject) => sum + subject.totalLessonsPlanned, 0);
    const totalCompleted = subjects.reduce((sum, subject) => sum + subject.lessonsCompleted, 0);
    const overallCompletion = totalPlanned > 0 ? (totalCompleted / totalPlanned) * 100 : 0;
    
    return {
      totalPlanned,
      totalCompleted,
      overallCompletion: Math.round(overallCompletion * 10) / 10
    };
  };

  // Simulate loading for better UX
  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      // Simulate API call delay
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  }, [isOpen]);

  const getCompletionColor = (percentage) => {
    if (percentage >= 80) return 'text-green-600 bg-green-50 border-green-200';
    if (percentage >= 60) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    return 'text-red-600 bg-red-50 border-red-200';
  };

  const getCompletionBadgeColor = (percentage) => {
    if (percentage >= 80) return 'bg-green-100 text-green-800';
    if (percentage >= 60) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  const downloadPDF = () => {
    generatePDFFromElement(reportRef, "Curriculum_Progress_Report.pdf");
  };

  const downloadExcel = () => {
    if (!dummyCurriculumProgressReport) return;

    const workbook = XLSX.utils.book_new();
    
    // Summary sheet
    const summaryData = [
      ["Curriculum Progress Report"],
      ["Campus", dummyCurriculumProgressReport.campusName],
      ["Class", dummyCurriculumProgressReport.className],
      ["Section", dummyCurriculumProgressReport.sectionName],
      ["Report Date", dummyCurriculumProgressReport.reportDate],
      [],
      ["Overall Summary"],
      ["Subject", "Teacher", "Total Lessons Planned", "Lessons Completed", "Completion %"],
    ];

    const overallStats = calculateOverallStats(dummyCurriculumProgressReport.subjects);
    
    dummyCurriculumProgressReport.subjects.forEach(subject => {
      summaryData.push([
        subject.subjectName,
        subject.teacher,
        subject.totalLessonsPlanned,
        subject.lessonsCompleted,
        `${subject.completionPercentage}%`
      ]);
    });

    summaryData.push([]);
    summaryData.push(["Overall Statistics"]);
    summaryData.push(["Total Lessons Planned", overallStats.totalPlanned]);
    summaryData.push(["Total Lessons Completed", overallStats.totalCompleted]);
    summaryData.push(["Overall Completion", `${overallStats.overallCompletion}%`]);

    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");

    // Detailed subject sheets
    dummyCurriculumProgressReport.subjects.forEach(subject => {
      const subjectData = [
        [`${subject.subjectName} - Detailed Progress`],
        ["Teacher", subject.teacher],
        ["Total Lessons Planned", subject.totalLessonsPlanned],
        ["Lessons Completed", subject.lessonsCompleted],
        ["Overall Completion", `${subject.completionPercentage}%`],
        [],
        ["Topic-wise Progress"],
        ["Topic", "Planned Lessons", "Completed Lessons", "Completion %"],
      ];

      subject.topics.forEach(topic => {
        subjectData.push([
          topic.name,
          topic.planned,
          topic.completed,
          `${topic.percentage}%`
        ]);
      });

      const subjectSheet = XLSX.utils.aoa_to_sheet(subjectData);
      XLSX.utils.book_append_sheet(workbook, subjectSheet, subject.subjectName.substring(0, 30));
    });

    XLSX.writeFile(workbook, "Curriculum_Progress_Report.xlsx");
  };

  if (!isOpen) return null;

  const overallStats = calculateOverallStats(dummyCurriculumProgressReport.subjects);

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div ref={reportRef} className="bg-white p-6 rounded-lg shadow-lg w-[95%] max-h-[95vh] overflow-y-auto relative border border-gray-300">
        {loading && <p>Loading curriculum progress report...</p>}

        {dummyCurriculumProgressReport ? (
          <>
            {/* Report Header */}
            <div className="text-center mb-6 border-b pb-4">
              <h1 className="text-2xl font-bold text-gray-800">{dummyCurriculumProgressReport.campusName}</h1>
              <h2 className="text-xl font-semibold mt-2">Curriculum Progress Report</h2>
              <div className="grid grid-cols-3 gap-4 mt-4 text-sm">
                <div><strong>Class:</strong> {dummyCurriculumProgressReport.className}</div>
                <div><strong>Section:</strong> {dummyCurriculumProgressReport.sectionName}</div>
                <div><strong>Report Date:</strong> {dummyCurriculumProgressReport.reportDate}</div>
              </div>
            </div>

            {/* Overall Summary Cards */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-4">Overall Summary</h3>
              <div className="grid grid-cols-4 gap-4 text-center">
                <div className="bg-blue-50 p-4 rounded">
                  <div className="text-2xl font-bold text-blue-600">
                    {dummyCurriculumProgressReport.subjects.length}
                  </div>
                  <div className="text-sm text-gray-600">Total Subjects</div>
                </div>
                <div className="bg-purple-50 p-4 rounded">
                  <div className="text-2xl font-bold text-purple-600">
                    {overallStats.totalPlanned}
                  </div>
                  <div className="text-sm text-gray-600">Total Lessons Planned</div>
                </div>
                <div className="bg-indigo-50 p-4 rounded">
                  <div className="text-2xl font-bold text-indigo-600">
                    {overallStats.totalCompleted}
                  </div>
                  <div className="text-sm text-gray-600">Lessons Completed</div>
                </div>
                <div className={`p-4 rounded border ${getCompletionColor(overallStats.overallCompletion)}`}>
                  <div className="text-2xl font-bold">
                    {overallStats.overallCompletion}%
                  </div>
                  <div className="text-sm">Overall Completion</div>
                </div>
              </div>
            </div>

            {/* Subject-wise Progress Table */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-4">Subject-wise Curriculum Progress</h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border border-gray-300 px-4 py-2 text-left">Subject</th>
                      <th className="border border-gray-300 px-4 py-2 text-left">Teacher</th>
                      <th className="border border-gray-300 px-4 py-2 text-center">Lessons Planned</th>
                      <th className="border border-gray-300 px-4 py-2 text-center">Lessons Completed</th>
                      <th className="border border-gray-300 px-4 py-2 text-center">Completion %</th>
                      <th className="border border-gray-300 px-4 py-2 text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dummyCurriculumProgressReport.subjects.map((subject, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="border border-gray-300 px-4 py-2 font-medium">
                          {subject.subjectName}
                        </td>
                        <td className="border border-gray-300 px-4 py-2">
                          {subject.teacher}
                        </td>
                        <td className="border border-gray-300 px-4 py-2 text-center">
                          {subject.totalLessonsPlanned}
                        </td>
                        <td className="border border-gray-300 px-4 py-2 text-center">
                          {subject.lessonsCompleted}
                        </td>
                        <td className="border border-gray-300 px-4 py-2 text-center">
                          <span className={`px-2 py-1 rounded text-sm font-medium ${getCompletionBadgeColor(subject.completionPercentage)}`}>
                            {subject.completionPercentage}%
                          </span>
                        </td>
                        <td className="border border-gray-300 px-4 py-2 text-center">
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full ${
                                subject.completionPercentage >= 80 ? 'bg-green-500' :
                                subject.completionPercentage >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                              }`}
                              style={{ width: `${subject.completionPercentage}%` }}
                            ></div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Detailed Topic-wise Progress */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-4">Detailed Topic-wise Progress</h3>
              <div className="space-y-6">
                {dummyCurriculumProgressReport.subjects.map((subject, subjectIndex) => (
                  <div key={subjectIndex} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="text-lg font-semibold text-gray-800">{subject.subjectName}</h4>
                      <div className="flex items-center space-x-4">
                        <span className="text-sm text-gray-600">
                          Teacher: <strong>{subject.teacher}</strong>
                        </span>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getCompletionBadgeColor(subject.completionPercentage)}`}>
                          {subject.completionPercentage}% Complete
                        </span>
                      </div>
                    </div>
                    
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse border border-gray-200">
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="border border-gray-200 px-3 py-2 text-left">Topic</th>
                            <th className="border border-gray-200 px-3 py-2 text-center">Planned</th>
                            <th className="border border-gray-200 px-3 py-2 text-center">Completed</th>
                            <th className="border border-gray-200 px-3 py-2 text-center">Progress</th>
                            <th className="border border-gray-200 px-3 py-2 text-center">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {subject.topics.map((topic, topicIndex) => (
                            <tr key={topicIndex} className="hover:bg-gray-25">
                              <td className="border border-gray-200 px-3 py-2">{topic.name}</td>
                              <td className="border border-gray-200 px-3 py-2 text-center">{topic.planned}</td>
                              <td className="border border-gray-200 px-3 py-2 text-center">{topic.completed}</td>
                              <td className="border border-gray-200 px-3 py-2">
                                <div className="flex items-center space-x-2">
                                  <div className="flex-1 bg-gray-200 rounded-full h-2">
                                    <div 
                                      className={`h-2 rounded-full ${
                                        topic.percentage >= 80 ? 'bg-green-500' :
                                        topic.percentage >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                                      }`}
                                      style={{ width: `${Math.min(topic.percentage, 100)}%` }}
                                    ></div>
                                  </div>
                                  <span className="text-xs font-medium min-w-[40px]">
                                    {Math.round(topic.percentage * 10) / 10}%
                                  </span>
                                </div>
                              </td>
                              <td className="border border-gray-200 px-3 py-2 text-center">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCompletionBadgeColor(topic.percentage)}`}>
                                  {topic.percentage >= 80 ? 'On Track' : 
                                   topic.percentage >= 60 ? 'Behind' : 'Critical'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Performance Analysis */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-4">Performance Analysis</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-green-50 p-4 rounded border border-green-200">
                  <h4 className="font-semibold text-green-800 mb-2">On Track (≥80%)</h4>
                  <div className="space-y-1">
                    {dummyCurriculumProgressReport.subjects
                      .filter(subject => subject.completionPercentage >= 80)
                      .map((subject, index) => (
                        <div key={index} className="text-sm text-green-700">
                          {subject.subjectName} ({subject.completionPercentage}%)
                        </div>
                      ))}
                  </div>
                </div>
                
                <div className="bg-yellow-50 p-4 rounded border border-yellow-200">
                  <h4 className="font-semibold text-yellow-800 mb-2">Behind Schedule (60-79%)</h4>
                  <div className="space-y-1">
                    {dummyCurriculumProgressReport.subjects
                      .filter(subject => subject.completionPercentage >= 60 && subject.completionPercentage < 80)
                      .map((subject, index) => (
                        <div key={index} className="text-sm text-yellow-700">
                          {subject.subjectName} ({subject.completionPercentage}%)
                        </div>
                      ))}
                  </div>
                </div>
                
                <div className="bg-red-50 p-4 rounded border border-red-200">
                  <h4 className="font-semibold text-red-800 mb-2">Critical (&lt;60%)</h4>
                  <div className="space-y-1">
                    {dummyCurriculumProgressReport.subjects
                      .filter(subject => subject.completionPercentage < 60)
                      .map((subject, index) => (
                        <div key={index} className="text-sm text-red-700">
                          {subject.subjectName} ({subject.completionPercentage}%)
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            </div>

          </>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500">No curriculum progress data available</p>
          </div>
        )}

        {/* Download Buttons */}
        <div className="no-print flex justify-end mt-6 space-x-4 border-t pt-4">
          <button onClick={downloadPDF} className="bg-blue-500 hover:bg-blue-600 px-4 py-2 rounded text-white transition-colors">
            Download as PDF
          </button>
          <button onClick={downloadExcel} className="bg-green-500 hover:bg-green-600 px-4 py-2 rounded text-white transition-colors">
            Download as Excel
          </button>
          <button onClick={onClose} className="bg-gray-400 hover:bg-gray-500 px-4 py-2 rounded text-white transition-colors">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewCurriculumProgressReportModal;